import axios from 'axios'
import {
  Program,
  ProgramGenerationRequest,
  GeneratedProgram,
  ProgramTemplate,
  ApiResponse,
  PaginatedResponse,
  MuscleActivationData
} from '@/types'
// Mock data removed to prevent UUID contamination

// API Configuration - Updated to use correct backend port
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://fit-backend-web.fly.dev'

const programApi = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Import the main API service for proper Auth0 token handling
import { apiService } from './api'

// Add auth token interceptor (fallback - prefer using apiService)
programApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export class ProgramService {
  // Program Generation
  static async generateProgram(request: ProgramGenerationRequest): Promise<GeneratedProgram> {
    try {
      console.log('🔄 Starting program generation request:', { request })
      const response = await apiService.generateProgram(request)
      console.log('✅ Program generation successful:', response)
      
      // Extract the program data from the nested response structure
      const programData = response.data?.data?.program || response.data?.program || response.data
      
      console.log('📋 Extracted program data:', programData)
      
      return programData
    } catch (error: any) {
      console.error('❌ Program generation failed:', {
        error: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers
      })
      throw error
    }
  }

  // Program CRUD Operations
  static async getPrograms(
    page: number = 1,
    limit: number = 10,
    filters?: {
      coachId?: string
      clientId?: string
      status?: Program['status']
      search?: string
    }
  ): Promise<PaginatedResponse<Program>> {
    try {
      // Use the main API service for proper Auth0 token handling
      const response = await apiService.getPrograms({
        page,
        limit,
        ...filters
      })
      
      console.log('API Response:', response) // Debug log
      
      if (response.success && response.data) {
        return {
          data: (response.data as any).programs || [],
          pagination: (response.data as any).pagination
        }
      } else {
        throw new Error(response.error || 'Failed to fetch programs')
      }
    } catch (error) {
      // No fallback to prevent UUID contamination - throw actual error
      console.error('❌ Programs API failed:', error)
      throw error
    }
  }

  static async getProgramById(id: string): Promise<Program> {
    try {
      // Use the main API service for proper Auth0 token handling
      const response = await apiService.getProgramById(id)
      
      if (response.success && response.data) {
        return response.data
      } else {
        throw new Error(response.error || 'Failed to fetch program')
      }
    } catch (error) {
      // No fallback to prevent UUID contamination - throw actual error
      console.error('❌ Program API failed:', error)
      throw error
    }
  }

  // Get workouts for a specific program
  static async getProgramWorkouts(programId: string): Promise<any[]> {
    try {
      console.log('Fetching workouts for program:', programId)
      
      // Use the main API service for proper Auth0 token handling
      const response = await apiService.getProgramWorkouts(programId)
      
      if (response.success && response.data) {
        // Backend returns { success: true, data: { workouts: [...] } }
        const workouts = response.data.workouts || response.data
        console.log('✅ Successfully fetched workouts from database:', workouts.length)
        return workouts
      } else {
        throw new Error(response.error || 'Workouts not found')
      }
    } catch (error: any) {
      console.error('Failed to fetch program workouts:', error)
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch program workouts')
    }
  }

  static async createProgram(program: Omit<Program, 'id' | 'createdAt' | 'updatedAt'>): Promise<Program> {
    const response = await programApi.post<ApiResponse<Program>>('/programs', program)
    return response.data.data
  }

  static async updateProgram(id: string, updates: Partial<Program>): Promise<Program> {
    const response = await programApi.put<ApiResponse<Program>>(`/programs/${id}`, updates)
    return response.data.data
  }

  static async deleteProgram(id: string): Promise<void> {
    await programApi.delete(`/programs/${id}`)
  }

  static async duplicateProgram(id: string, options?: {
    name?: string
    clientId?: string
  }): Promise<Program> {
    const response = await programApi.post<ApiResponse<Program>>(`/programs/${id}/duplicate`, options)
    return response.data.data
  }

  // Program Templates
  static async getTemplates(
    category?: ProgramTemplate['category'],
    difficulty?: ProgramTemplate['difficulty']
  ): Promise<ProgramTemplate[]> {
    const params = new URLSearchParams()
    if (category) params.append('category', category)
    if (difficulty) params.append('difficulty', difficulty)
    
    const response = await programApi.get<ApiResponse<ProgramTemplate[]>>(`/templates?${params}`)
    return response.data.data
  }

  static async createTemplate(template: Omit<ProgramTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<ProgramTemplate> {
    const response = await programApi.post<ApiResponse<ProgramTemplate>>('/templates', template)
    return response.data.data
  }

  static async createProgramFromTemplate(
    templateId: string,
    options: {
      clientId: string
      name: string
      startDate: Date
      customizations?: Partial<Program>
    }
  ): Promise<Program> {
    const response = await programApi.post<ApiResponse<Program>>(`/templates/${templateId}/create-program`, options)
    return response.data.data
  }

  // Exercise and Muscle Data
  static async getMuscleActivations(): Promise<MuscleActivationData[]> {
    try {
      const response = await programApi.get<ApiResponse<MuscleActivationData[]>>('/muscle-activations')
      return response.data.data
    } catch (error) {
      // No fallback to prevent UUID contamination - throw actual error
      console.error('❌ Muscle activations API failed:', error)
      throw error
    }
  }

  static async getExerciseLibrary(filters?: {
    muscleGroup?: string
    equipment?: string
    category?: string
    search?: string
  }): Promise<any[]> {
    try {
      const params = new URLSearchParams(filters as any)
      const response = await programApi.get(`/exercises?${params}`)
      return response.data.data
    } catch (error) {
      // Fallback to mock data
      console.warn('API not available, using mock data:', error)
      let filteredExercises: any[] = []
      
      if (filters?.muscleGroup) {
        filteredExercises = filteredExercises.filter(e => 
          e.muscleGroups?.includes(filters.muscleGroup as any)
        )
      }
      if (filters?.equipment) {
        filteredExercises = filteredExercises.filter(e => 
          e.equipment?.includes(filters.equipment as any)
        )
      }
      if (filters?.search) {
        const search = filters.search.toLowerCase()
        filteredExercises = filteredExercises.filter(e => 
          e.name.toLowerCase().includes(search)
        )
      }
      
      return filteredExercises
    }
  }

  // Program Analytics and Insights
  static async getProgramAnalytics(programId: string): Promise<{
    completionRate: number
    averageRating: number
    progressMetrics: any[]
    adherenceData: any[]
    strengthProgression: any[]
  }> {
    const response = await programApi.get(`/programs/${programId}/analytics`)
    return response.data.data
  }

  static async getProgramSuggestions(clientId: string): Promise<{
    recommendations: ProgramTemplate[]
    reasoning: string[]
    muscleImbalances?: string[]
    progressionNeeds?: string[]
  }> {
    const response = await programApi.get(`/suggestions/programs/${clientId}`)
    return response.data.data
  }

  // Program Progression and Adaptations
  static async adaptProgram(
    programId: string,
    adaptations: {
      type: 'progression' | 'regression' | 'plateau' | 'injury'
      reason: string
      changes: any[]
    }
  ): Promise<Program> {
    const response = await programApi.post<ApiResponse<Program>>(`/programs/${programId}/adapt`, adaptations)
    return response.data.data
  }

  static async autoProgressProgram(programId: string): Promise<Program> {
    const response = await programApi.post<ApiResponse<Program>>(`/programs/${programId}/auto-progress`)
    return response.data.data
  }

  // Batch Operations
  static async bulkUpdatePrograms(
    programIds: string[],
    updates: Partial<Program>
  ): Promise<Program[]> {
    const response = await programApi.put<ApiResponse<Program[]>>('/programs/bulk', {
      programIds,
      updates
    })
    return response.data.data
  }

  static async exportProgram(programId: string, format: 'pdf' | 'json' | 'csv'): Promise<Blob> {
    const response = await programApi.get(`/programs/${programId}/export/${format}`, {
      responseType: 'blob'
    })
    return response.data
  }

  // Real-time Collaboration (for future implementation)
  static async subscribeToProgramUpdates(_programId: string, _callback: (update: any) => void): Promise<() => void> {
    // WebSocket implementation would go here
    // For now, return a no-op unsubscribe function
    return () => {}
  }

  // Save workout schedule assignments
  static async saveWorkoutSchedule(programId: string, assignments: Array<{
    workoutId: string
    assignedDate: Date
  }>): Promise<void> {
    try {
      const workoutAssignments = assignments.map(assignment => ({
        workoutId: assignment.workoutId,
        assignedDate: assignment.assignedDate.toISOString()
      }))
      
      console.log('💾 Saving workout schedule:', { programId, workoutAssignments })
      
      // Use the main API service for proper Auth0 token handling
      const response = await apiService.assignWorkoutDates(programId, workoutAssignments)
      
      if (response.success) {
        console.log('✅ Workout schedule saved successfully:', response)
      } else {
        console.error('❌ Workout schedule save failed:', response)
        throw new Error(response.error || 'Failed to save workout schedule')
      }
    } catch (error: any) {
      console.error('❌ Failed to save workout schedule:', error)
      throw new Error(error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to save workout schedule')
    }
  }
}

// Utility functions for common operations
export const programUtils = {
  calculateProgramDuration: (weeks: number, workoutsPerWeek: number) => {
    return {
      totalWorkouts: weeks * workoutsPerWeek,
      estimatedHours: weeks * workoutsPerWeek * 1.5, // Assuming 90 minutes per workout
      weeks
    }
  },

  generateProgramName: (client: any, focus: string[], duration: number) => {
    const focusString = focus.slice(0, 2).join(' & ')
    return `${client.name} - ${focusString} Program (${duration}w)`
  },

  validateProgramStructure: (program: Partial<Program>) => {
    const errors: string[] = []
    
    if (!program.name?.trim()) {
      errors.push('Program name is required')
    }
    
    if (!program.duration || program.duration < 1) {
      errors.push('Program duration must be at least 1 week')
    }
    
    if (!program.workoutsPerWeek || program.workoutsPerWeek < 1) {
      errors.push('At least 1 workout per week is required')
    }
    
    if (program.weeks && program.weeks.length !== program.duration) {
      errors.push('Number of weeks must match program duration')
    }
    
    return {
      isValid: errors.length === 0,
      errors
    }
  },

  formatProgramProgress: (program: Program) => {
    if (!program.progress) return { percentage: 0, status: 'Not Started' }
    
    const completionRate = program.progress.completionRate
    
    if (completionRate === 0) return { percentage: 0, status: 'Not Started' }
    if (completionRate < 25) return { percentage: completionRate, status: 'Just Started' }
    if (completionRate < 50) return { percentage: completionRate, status: 'Making Progress' }
    if (completionRate < 75) return { percentage: completionRate, status: 'Halfway There' }
    if (completionRate < 100) return { percentage: completionRate, status: 'Almost Done' }
    return { percentage: 100, status: 'Completed' }
  }
}